// Contrail V3.11.0 — reads platform notices from /api/status (Netlify, not Supabase) for the banner and sign-in page.
// One shared poll for the whole page (every 60 s, and when the tab comes back). The last answer is kept in
// localStorage so a notice still shows if the status service can't be reached; expired ones drop out by the clock.
import { useEffect, useState } from "react";
import { isVisible, type PublicNotice } from "./statusNotice";

const CACHE = "fw.status";
const POLL_MS = 60_000;

type Listener = (n: PublicNotice[]) => void;
let current: PublicNotice[] = readCache();
const listeners = new Set<Listener>();
let timer: ReturnType<typeof setInterval> | null = null;
let inflight: Promise<void> | null = null;

function readCache(): PublicNotice[] {
  try { const v = JSON.parse(localStorage.getItem(CACHE) ?? "null"); return Array.isArray(v?.notices) ? v.notices : []; }
  catch { return []; }
}
const live = (list: PublicNotice[]) => list.filter((n) => isVisible({ ended_at: null, ends_at: n.ends_at, announce_from: null }));

function publish(list: PublicNotice[]) {
  current = list;
  const shown = live(list);
  listeners.forEach((l) => l(shown));
}

export function refreshStatus(): Promise<void> {
  inflight ??= (async () => {
    try {
      const r = await fetch("/api/status", { cache: "no-store", headers: { Accept: "application/json" } });
      if (!r.ok) throw new Error(String(r.status));
      const body = await r.json();
      const list: PublicNotice[] = Array.isArray(body?.notices) ? body.notices : [];
      try { localStorage.setItem(CACHE, JSON.stringify({ notices: list, at: Date.now() })); } catch { /* storage off */ }
      publish(list);
    } catch {
      publish(current);                     // keep the last known notices (re-checked against the clock)
    } finally { inflight = null; }
  })();
  return inflight;
}

function onVisible() { if (document.visibilityState === "visible") void refreshStatus(); }

/** Notices showing now. `where` filters to the app banner or the sign-in page. */
export function useStatusNotices(where: "app" | "login") {
  const [list, setList] = useState<PublicNotice[]>(() => live(current));
  useEffect(() => {
    const l: Listener = (n) => setList(n);
    listeners.add(l);
    if (listeners.size === 1) {
      void refreshStatus();
      timer = setInterval(() => void refreshStatus(), POLL_MS);
      document.addEventListener("visibilitychange", onVisible);
    }
    return () => {
      listeners.delete(l);
      if (!listeners.size) { if (timer) clearInterval(timer); timer = null; document.removeEventListener("visibilitychange", onVisible); }
    };
  }, []);
  return list.filter((n) => (where === "app" ? n.show_app : n.show_login));
}
